// @flow

/**
 * filmStripViewManager.js
 *
 * Centralized module for ALL filmstrip view state management logic.
 * Every piece of filmstrip layout switching logic across the app is consolidated here.
 *
 * This file contains:
 * 1. The core action creator (filmStripViewStateHandler)
 * 2. Utility functions for computing target filmstrip views
 * 3. High-level handler functions for specific scenarios (pin, sharing, PiP, responsive, etc.)
 *
 * All caller files should import from this module.
 *
 * NOTE: video-layout is imported from its leaf modules (actions/constants), never through the feature index. That
 * index also re-exports components, and TileViewButton imports trackUserInteraction from this file - the require
 * cycle that creates is resolved differently by Metro than by webpack and left this module's exports undefined at
 * call time on mobile only ('undefined is not a function' while handling the WHITEBOARD_COMMAND presence node).
 */

import { jitsiLocalStorage } from '@jitsi/js-utils';

import { getPictureInPictureModeConfig } from '../base/config/functions.any';
import { getWindowWidth } from '../base/environment/utils';
import { MEDIA_TYPE } from '../base/media';
import { getAppState } from '../base/redux';
import { isVideoPlaying } from '../shared-video/functions';
import { LAYOUTS } from '../video-layout';
import { setTileView } from '../video-layout/actions';
import { getEffectiveLayout, getVisibleParticipantCount } from '../video-layout/functions';

import { FILM_STRIP_VIEW } from './actionTypes';
import { getRecorderTargetView } from './recorderViewHelpers';

/* eslint-disable max-params */

declare var config: Object;

// State tracking for Tile View auto-switch & user manual layout change during presentation/sharing
let autoSwitchedFromTileView = false;
let userChangedLayout = false;
let isSharingActiveState = false;

/**
 * Registers that the user manually changed or requested a filmstrip layout
 * (e.g., via SelectLabel, TileViewButton, keyboard shortcut, or manual pin).
 *
 * @returns {void}
 */
export function trackUserInteraction() {
    if (isSharingActiveState) {
        userChangedLayout = true;
    }
}

/**
 * Called when a presentation sharing session starts (screenshare, whiteboard, youtube).
 * Checks if current layout is Tile View and sets up tracking for auto-switch restoration.
 *
 * @param {Object} state - The Redux state.
 * @param {string} [currentFilmStripView] - Optional current filmStripView string.
 * @returns {void}
 */
export function onPresentationSharingStarted(state: Object, currentFilmStripView?: string) {
    if (!isSharingActiveState) {
        isSharingActiveState = true;
        const currentView = currentFilmStripView || state['features/base/conference']?.filmStripView;
        const tileViewEnabled = state['features/video-layout']?.tileViewEnabled;
        const storedView = jitsiLocalStorage.getItem('filmStripView');
        const wasInTileView = currentView === LAYOUTS.TILE_VIEW
            || tileViewEnabled === true
            || storedView === LAYOUTS.TILE_VIEW;

        autoSwitchedFromTileView = wasInTileView;
        userChangedLayout = false;
    }
}

/**
 * Called when ALL presentation sharing sessions have stopped.
 * If auto-switched from Tile View and user did NOT manually change layout during presentation,
 * restores Tile View layout.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {boolean} True if Tile View was restored.
 */
export function onPresentationSharingStopped(dispatch: Function): boolean {
    let restoredTile = false;

    if (autoSwitchedFromTileView && !userChangedLayout) {
        dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW, 'onPresentationSharingStopped_RestoreTile'));
        dispatch(setTileView(true));
        restoredTile = true;
    }

    isSharingActiveState = false;
    autoSwitchedFromTileView = false;
    userChangedLayout = false;

    return restoredTile;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1: MAP STATETOPROPS & CORE ACTION CREATOR
// ═══════════════════════════════════════════════════════════════════════════════


/**
 * Maps Redux state to props related to filmstrip views and layout management.
 * Can be used directly by components or internally inside manager functions
 * when state arguments are omitted.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object}
 */
export function mapStateToProps(state: Object) {
    const { filmStripView, objectFit } = state['features/base/conference'] || {};
    const { iAmRecorder } = state['features/base/config'] || {};
    const isPiPEnabled = getPictureInPictureModeConfig(state);
    const participants = state['features/base/participants'] || [];
    const isScreenSharing = participants.some(p => p.isFakeParticipant === 'LocalScreenShare' || p.isFakeParticipant === 'RemoteScreenShare');
    const screenSharingParticipant = participants.find(p => p.isFakeParticipant === 'LocalScreenShare' || p.isFakeParticipant === 'RemoteScreenShare');
    const isSharedVideoVisible = isVideoPlaying(state);
    const participantCount = getVisibleParticipantCount(state);
    const isIframeSharing = Boolean(state['features/genericiframe']?.visible || state['features/etherpad']?.editing);

    return {
        filmStripView,
        _objectFit: objectFit,
        _iAmRecorder: iAmRecorder || Boolean(typeof config !== 'undefined' && config.iAmRecorder),
        _isPictureInPictureEnabled: isPiPEnabled,
        _isIframeSharing: isIframeSharing,
        _isScreenSharing: isScreenSharing,
        _screenSharingParticipantId: screenSharingParticipant?.id,
        _isSharedVideoVisible: isSharedVideoVisible,
        _participantCount: participantCount
    };
}

/**
 * Set the value of _filmStripView in the Redux store.
 * This is the primary action creator for changing the filmstrip layout.
 *
 * Contains built-in logic to override VERTICAL_FILMSTRIP_VIEW → HORIZONTAL_FILMSTRIP_VIEW
 * when PiP mode is enabled or window width is < 500px.
 *
 * @param {string} value - The target layout (one of LAYOUTS.*).
 * @param {string} [status] - Optional debug label for console logging.
 * @returns {{ type: string, value: string }}
 */
export function filmStripViewStateHandler(value: string) {
    const state = getAppState();
    const effectiveValue = getEffectiveLayout(state, value);

    return {
        type: FILM_STRIP_VIEW,
        value: effectiveValue
    };
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2: UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns the user's preferred filmstrip view from localStorage, validating against allowed layouts.
 * If the stored value is TILE_VIEW or disallowed, it picks the preferred allowed non-tile layout.
 * If no non-tile layout is allowed, it returns TILE_VIEW (or whatever layout is allowed).
 *
 * @param {Object} [state] - The Redux state.
 * @returns {string} One of LAYOUTS.* constants.
 */
export function getStoredFilmStripView(state?: Object): string {
    const currentState = state || getAppState();
    const stored = jitsiLocalStorage.getItem('filmStripView');
    const desired = stored && stored !== LAYOUTS.TILE_VIEW ? stored : LAYOUTS.VERTICAL_FILMSTRIP_VIEW;

    return getEffectiveLayout(currentState, desired);
}

/**
 * Returns the appropriate filmstrip view for the current context, considering
 * whether the app is running as a recorder.
 *
 * - For recorders: uses getRecorderTargetView() which factors in priority content
 * - For normal users: uses getStoredFilmStripView() from localStorage
 *
 * @param {Object} state - The Redux state.
 * @returns {string} One of LAYOUTS.* constants.
 */
export function getRecorderOrStoredFilmStripView(state: Object): string {
    const { iAmRecorder } = state['features/base/config'] || {};
    const isRecorder = iAmRecorder || (typeof config !== 'undefined' && config?.iAmRecorder);

    if (isRecorder) {
        return getRecorderTargetView(state);
    }

    return getStoredFilmStripView(state);
}


/**
 * Computes the appropriate filmstrip view for sharing scenarios
 * (screen sharing or iframe sharing) and pinned participants. Accounts for:
 * - Toolbar buttons allowed layouts
 * - Recorder mode
 * - PiP mode
 * - Mobile/narrow screen widths
 *
 * @param {Object} state - The Redux state.
 * @returns {string} One of LAYOUTS.* constants.
 */
export function getSharingTargetFilmstripView(state: Object): string {
    const currentState = state || getAppState();

    if (typeof config !== 'undefined' && config.iAmRecorder) {
        return getRecorderTargetView(currentState);
    }

    const stored = jitsiLocalStorage.getItem('filmStripView');
    const desired = stored && stored !== LAYOUTS.TILE_VIEW ? stored : LAYOUTS.VERTICAL_FILMSTRIP_VIEW;

    return getEffectiveLayout(currentState, desired);
}

/**
 * Checks whether any presentation or screen sharing session is currently active
 * (screen sharing, shared video, whiteboard, etherpad).
 *
 * @param {Object} [state] - The Redux state.
 * @returns {boolean} True if presentation or sharing is active.
 */
export function isPresentationOrSharingActive(state?: Object): boolean {
    if (isSharingActiveState) {
        return true;
    }

    const currentState = state || getAppState();
    const tracks = currentState['features/base/tracks'] || [];
    const hasScreenShareTrack = tracks.some(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && !t.muted);
    const participants = currentState['features/base/participants'] || [];
    const isScreenSharing = hasScreenShareTrack
        || participants.some(p => p.isFakeParticipant === 'LocalScreenShare' || p.isFakeParticipant === 'RemoteScreenShare');
    const isSharedVideoVisible = isVideoPlaying(currentState);
    const isIframeSharing = Boolean(currentState['features/genericiframe']?.visible || currentState['features/etherpad']?.editing);

    return Boolean(isScreenSharing || isSharedVideoVisible || isIframeSharing);
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3: HANDLER FUNCTIONS — Thumbnail / Pin Actions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * If the current filmstrip view is TILE_VIEW, exits tile view by switching
 * to the user's stored preferred layout.
 *
 * Used by: SharedThumbnail, SharedVideoThumbnail, SmallVideo
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} currentFilmStripView - The current filmStripView from state.
 * @returns {void}
 */
export function exitTileViewIfNeeded(dispatch: Function, currentFilmStripView: string) {
    if (currentFilmStripView === LAYOUTS.TILE_VIEW) {
        const state = getAppState();
        const filmstripview = getSharingTargetFilmstripView(state, false);

        if (filmstripview !== LAYOUTS.TILE_VIEW) {
            dispatch(filmStripViewStateHandler(filmstripview));
        }
    }
}

/**
 * Handles filmstrip view transition when a participant is pinned.
 * Accounts for recorder mode (uses getRecorderTargetView) vs normal mode
 * (uses stored preference from localStorage, respecting allowed toolbar buttons).
 *
 * Used by: base/conference/middleware.any.js (PIN_PARTICIPANT),
 *          video-layout/middleware.web.js (SET_MODERATOR_PIN)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} state - The Redux state.
 * @param {string} debugLabel - Debug label for logging.
 * @returns {void}
 */
export function handlePinParticipantFilmstrip(
        dispatch: Function,
        state: Object,
        debugLabel: string,
        actionParticipant?: Object
) {
    const currentState = state || getAppState();
    const { iAmRecorder } = currentState['features/base/config'] || {};
    const isRecorder = iAmRecorder || (typeof config !== 'undefined' && config?.iAmRecorder);

    if (isRecorder) {
        const filmstripviewlocal = getRecorderTargetView(currentState);

        dispatch(filmStripViewStateHandler(filmstripviewlocal, debugLabel));
    } else {
        const isUnpin = Boolean(actionParticipant && (!actionParticipant.id || actionParticipant.isPinned === false));

        if (isUnpin) {
            // While any presentation or screen sharing is active, unpinning should NEVER switch to Tile View.
            // Keep the appropriate sharing layout (e.g. vertical or horizontal).
            if (isPresentationOrSharingActive(currentState)) {
                const isPiP = getPictureInPictureModeConfig(currentState);
                const filmstripview = getSharingTargetFilmstripView(currentState, isPiP);

                dispatch(filmStripViewStateHandler(filmstripview, debugLabel));

                return;
            }

            const currentFilmstrip = currentState['features/base/conference']?.filmStripView;
            const tileEnabled = currentState['features/video-layout']?.tileViewEnabled;
            const storedLayout = jitsiLocalStorage.getItem('filmStripView');

            if (currentFilmstrip === LAYOUTS.TILE_VIEW || tileEnabled || storedLayout === LAYOUTS.TILE_VIEW) {
                dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW, debugLabel));
                dispatch(setTileView(true));

                return;
            }

            dispatch(filmStripViewStateHandler(getStoredFilmStripView(currentState), debugLabel));

            return;
        }

        const isPiP = getPictureInPictureModeConfig(currentState);
        const filmstripview = getSharingTargetFilmstripView(currentState, isPiP);

        dispatch(filmStripViewStateHandler(filmstripview, debugLabel));
    }
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 4: HANDLER FUNCTIONS — Conference Lifecycle
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Handles initial filmstrip setup for the recorder bot in Conference.componentDidMount.
 * Computes the correct target view and dispatches if different from current.
 *
 * Used by: Conference.js (componentDidMount)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} currentFilmStripView - The current filmStripView from props.
 * @param {Object} state - The Redux state.
 * @returns {string} The computed target view (for further logic like tile limit setting).
 */
export function handleRecorderViewSetup(dispatch: Function, currentFilmStripView: string, state: Object): string {
    const targetView = getRecorderTargetView(state);

    if (currentFilmStripView !== targetView) {
        dispatch(filmStripViewStateHandler(targetView));
    }

    return targetView;
}

/**
 * Handles PiP (Picture-in-Picture) mode filmstrip transitions.
 * When PiP is enabled: saves current filmstrip and forces horizontal only if currently in vertical view.
 * When PiP is disabled: restores previously saved filmstrip.
 *
 * Used by: ConferenceLifecycleHelpers.js
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} instance - The Conference component instance.
 * @param {boolean} [isPiPEnabled] - Whether PiP is currently enabled.
 * @param {string} [filmStripView] - Current filmstrip view.
 * @returns {void}
 */
export function handlePipFilmstripTransition(
        dispatch: Function,
        instance: Object,
        isPiPEnabled?: boolean,
        filmStripView?: string
) {
    const state = getAppState();
    const mapped = mapStateToProps(state);
    const currentIsPiP = typeof isPiPEnabled === 'undefined' ? mapped._isPictureInPictureEnabled : isPiPEnabled;
    const currentView = typeof filmStripView === 'undefined' ? mapped.filmStripView : filmStripView;

    if (currentIsPiP) {
        const isSharing = isPresentationOrSharingActive(state);

        // PiP enabled:
        // - Shift to horizontal if currently in vertical filmstrip view.
        // - If in tile view, only shift to horizontal if screen sharing/presentation is active.
        if (currentView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW || (currentView === LAYOUTS.TILE_VIEW && isSharing)) {
            instance.pipFilmstrip = currentView;
            dispatch(filmStripViewStateHandler(LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW));
        }
    } else if (instance.pipFilmstrip) {
        // PiP disabled — restore saved filmstrip
        dispatch(filmStripViewStateHandler(instance.pipFilmstrip));
        instance.pipFilmstrip = undefined;
    }
}

/**
 * Handles responsive filmstrip layout adjustments based on window dimensions.
 * Called from ConferenceLifecycleHelpers on componentDidUpdate.
 *
 * Rules:
 * - If height < 440 && width >= 500 && horizontal && !PiP && !recorder → switch to vertical (even if not in toolbar buttons)
 * - If width < 500 && vertical → switch to horizontal (even if not in toolbar buttons)
 * - Restores configured toolbar button layout when dimensions return to normal
 *
 * Used by: ConferenceLifecycleHelpers.js
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} filmStripView - The current filmstrip view.
 * @param {boolean} isPiPEnabled - Whether PiP mode is enabled.
 * @returns {void}
 */
export function handleResponsiveLayoutCheck(dispatch: Function, filmStripView?: string, isPiPEnabled?: boolean) {
    const state = getAppState();
    const mapped = mapStateToProps(state);
    const currentView = typeof filmStripView === 'undefined' ? mapped.filmStripView : filmStripView;
    const currentIsPiP = typeof isPiPEnabled === 'undefined' ? mapped._isPictureInPictureEnabled : isPiPEnabled;
    const isRecorder = typeof config !== 'undefined' && config.iAmRecorder;

    if (typeof window === 'undefined') {
        return;
    }

    const isNarrow = getWindowWidth() < 500;
    const isShortHeight = !currentIsPiP && !isRecorder && window.innerHeight < 440 && window.innerWidth >= 500;

    // Landscape with short height: switch horizontal → vertical
    if (isShortHeight && currentView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW) {
        dispatch(filmStripViewStateHandler(LAYOUTS.VERTICAL_FILMSTRIP_VIEW, 'ConferenceLifecycleHelpers_129'));
    }

    // Narrow screen: switch vertical → horizontal
    if (isNarrow && currentView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW) {
        dispatch(filmStripViewStateHandler(LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW));
    }
}

/**
 * Handles participant count changes and their effect on filmstrip view.
 *
 * Rules:
 * - ≤1 participant in tile view && !recorder && !PiP → switch to stage view
 * - ≥2 participants (from 1) && !recorder && not in tile && no sharing active && !PiP → switch to tile view
 *
 * Used by: ConferenceLifecycleHelpers.js
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} options - Configuration options.
 * @param {number} options.participantCount - Current participant count.
 * @param {number} options.prevParticipantCount - Previous participant count.
 * @param {string} options.filmStripView - Current filmstrip view.
 * @param {boolean} options.isPiPEnabled - Whether PiP mode is enabled.
 * @param {boolean} options.isIframeSharing - Whether iframe (whiteboard/etherpad) is active.
 * @param {boolean} options.isScreenSharing - Whether screen sharing is active.
 * @param {boolean} options.isSharedVideoVisible - Whether shared video is visible.
 * @returns {void}
 */
export function handleParticipantCountFilmstrip(dispatch: Function, options?: Object) {
    const state = getAppState();
    const mapped = mapStateToProps(state);
    const {
        participantCount = mapped._participantCount,
        prevParticipantCount = mapped._participantCount,
        filmStripView = mapped.filmStripView,
        isPiPEnabled = mapped._isPictureInPictureEnabled,
        isIframeSharing = mapped._isIframeSharing,
        isScreenSharing = mapped._isScreenSharing,
        isSharedVideoVisible = mapped._isSharedVideoVisible
    } = options || {};

    const isRecorder = typeof config !== 'undefined' && config.iAmRecorder;

    if (participantCount <= 1 && filmStripView === LAYOUTS.TILE_VIEW && !isRecorder) {
        dispatch(filmStripViewStateHandler(LAYOUTS.STAGE_VIEW));
    } else if (participantCount >= 2 && prevParticipantCount === 1
        && !isRecorder && filmStripView !== LAYOUTS.TILE_VIEW
        && !isIframeSharing && !isScreenSharing && !isSharedVideoVisible && !isPiPEnabled) {
        dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW));
    }
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 5: HANDLER FUNCTIONS — Sharing (Screen Share / Iframe)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Handles filmstrip transitions when iframe sharing (whiteboard/etherpad) starts.
 * If currently in tile view, exits to the appropriate view.
 * Accounts for PiP mode (saves to pipFilmstrip instead of dispatching).
 *
 * Used by: ConferenceLifecycleHelpers.js (iframeSharingStarted)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} instance - The Conference component instance.
 * @param {string} filmStripView - Current filmstrip view.
 * @param {boolean} isPiPEnabled - Whether PiP mode is enabled.
 * @returns {void}
 */
export function handleIframeSharingStartFilmstrip(
        dispatch: Function,
        instance: Object,
        filmStripView?: string,
        isPiPEnabled?: boolean
) {
    const state = getAppState();

    onPresentationSharingStarted(state);

    const mapped = mapStateToProps(state);
    const currentView = typeof filmStripView === 'undefined' ? mapped.filmStripView : filmStripView;
    const currentIsPiP = typeof isPiPEnabled === 'undefined' ? mapped._isPictureInPictureEnabled : isPiPEnabled;

    if (currentView === LAYOUTS.TILE_VIEW) {
        let filmstripview;

        if (typeof config !== 'undefined' && config.iAmRecorder) {
            filmstripview = getRecorderTargetView(state);
        } else {
            filmstripview = getSharingTargetFilmstripView(state, currentIsPiP);
        }

        if (filmstripview !== LAYOUTS.TILE_VIEW) {
            if (currentIsPiP) {
                instance.pipFilmstrip = currentView;
                dispatch(filmStripViewStateHandler(LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW, 'iframeSharingStarted_PiP'));
            } else {
                dispatch(filmStripViewStateHandler(filmstripview, 'iframeSharingStarted'));
            }
        }
    }
}

/**
 * Handles filmstrip transitions when screen sharing starts.
 * If currently in tile view, exits to the appropriate view.
 * Accounts for PiP mode, recorder mode, and mobile width.
 *
 * Used by: ConferenceLifecycleHelpers.js (screenSharingStarted)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} instance - The Conference component instance.
 * @param {string} filmStripView - Current filmstrip view.
 * @param {boolean} isPiPEnabled - Whether PiP mode is enabled.
 * @param {boolean} screenSharingStarted - Whether this is a screen sharing start event.
 * @returns {void}
 */
export function handleScreenSharingStartFilmstrip(
        dispatch: Function,
        instance: Object,
        filmStripView?: string,
        isPiPEnabled?: boolean,
        screenSharingStarted?: boolean = true
) {
    const state = getAppState();
    const mapped = mapStateToProps(state);
    const currentView = typeof filmStripView === 'undefined' ? mapped.filmStripView : filmStripView;
    const currentIsPiP = typeof isPiPEnabled === 'undefined' ? mapped._isPictureInPictureEnabled : isPiPEnabled;

    if (screenSharingStarted) {
        onPresentationSharingStarted(state, currentView);
    }

    const filmstripview = getSharingTargetFilmstripView(state, currentIsPiP);

    if (currentView === LAYOUTS.TILE_VIEW && screenSharingStarted && filmstripview !== LAYOUTS.TILE_VIEW) {
        if (currentIsPiP) {
            instance.pipFilmstrip = currentView;
            dispatch(filmStripViewStateHandler(LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW, 'screenSharingStarted_PiP'));
        } else {
            dispatch(filmStripViewStateHandler(filmstripview, 'screenSharingStarted'));
        }
    }
}

/**
 * Handles filmstrip restoration when iframe sharing (whiteboard/etherpad) stops.
 * If screen sharing is still active, transitions to screenshare mode.
 * Otherwise, restores the saved filmstrip view.
 * Accounts for PiP and recorder mode.
 *
 * Used by: ConferenceLifecycleHelpers.js (iframeShareingStopped)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} instance - The Conference component instance.
 * @param {Object} [sharingState] - The current sharing state from component state.
 * @param {boolean} [isPiPEnabled] - Whether PiP mode is enabled.
 * @returns {void}
 */
export function handleIframeSharingStopFilmstrip(
        dispatch: Function,
        instance: Object,
        sharingState?: Object,
        isPiPEnabled?: boolean
): boolean {
    const state = getAppState();
    const mapped = mapStateToProps(state);
    const currentSharingState = typeof sharingState === 'undefined' ? instance.state ? instance.state.sharingState : {} : sharingState;
    const currentIsPiP = typeof isPiPEnabled === 'undefined' ? mapped._isPictureInPictureEnabled : isPiPEnabled;

    let { savedFilmstrip } = currentSharingState || {};

    if (typeof config !== 'undefined' && config.iAmRecorder) {
        savedFilmstrip = getRecorderTargetView(state);
    } else if (!savedFilmstrip && instance.pipFilmstrip) {
        savedFilmstrip = instance.pipFilmstrip;
    }

    if (savedFilmstrip) {
        if (currentIsPiP) {
            instance.pipFilmstrip = savedFilmstrip;
            if (savedFilmstrip === LAYOUTS.TILE_VIEW && !userChangedLayout) {
                dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW, 'iframeSharingStopped_RestoreTile_PiP'));
                dispatch(setTileView(true));
            }
        } else if (savedFilmstrip === LAYOUTS.TILE_VIEW) {
            if (!userChangedLayout) {
                dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW, 'iframeSharingStopped_RestoreTile'));
                dispatch(setTileView(true));
            }

            return onPresentationSharingStopped(dispatch) || !userChangedLayout;
        } else {
            dispatch(filmStripViewStateHandler(savedFilmstrip, 'iframeShareingStopped'));
        }
    } else {
        return onPresentationSharingStopped(dispatch);
    }

    return false;
}

/**
 * Handles filmstrip restoration when screen sharing stops.
 * Restores the saved filmstrip view.
 * Accounts for PiP and recorder mode.
 *
 * Used by: ConferenceLifecycleHelpers.js (screenSharingStopped)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} instance - The Conference component instance.
 * @param {Object} [sharingState] - The current sharing state from component state.
 * @param {boolean} [isPiPEnabled] - Whether PiP mode is enabled.
 * @returns {boolean} True if Tile View was restored.
 */
export function handleScreenSharingStopFilmstrip(
        dispatch: Function,
        instance: Object,
        sharingState?: Object,
        isPiPEnabled?: boolean
): boolean {
    const state = getAppState();
    const mapped = mapStateToProps(state);
    const currentSharingState = typeof sharingState === 'undefined' ? instance.state ? instance.state.sharingState : {} : sharingState;
    const currentIsPiP = typeof isPiPEnabled === 'undefined' ? mapped._isPictureInPictureEnabled : isPiPEnabled;

    let { savedFilmstrip } = currentSharingState || {};

    if (typeof config !== 'undefined' && config.iAmRecorder) {
        savedFilmstrip = getRecorderTargetView(state);
    } else if (!savedFilmstrip && instance.pipFilmstrip) {
        savedFilmstrip = instance.pipFilmstrip;
    }

    if (savedFilmstrip) {
        if (currentIsPiP) {
            instance.pipFilmstrip = savedFilmstrip;
            if (savedFilmstrip === LAYOUTS.TILE_VIEW && !userChangedLayout) {
                dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW, 'screenSharingStopped_RestoreTile_PiP'));
                dispatch(setTileView(true));
            }
        } else if (savedFilmstrip === LAYOUTS.TILE_VIEW) {
            if (!userChangedLayout) {
                dispatch(filmStripViewStateHandler(LAYOUTS.TILE_VIEW, 'screenSharingStopped_RestoreTile'));
                dispatch(setTileView(true));
            }

            return onPresentationSharingStopped(dispatch) || !userChangedLayout;
        } else {
            const filmstripView = currentIsPiP
                ? LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW
                : savedFilmstrip;

            dispatch(filmStripViewStateHandler(filmstripView, 'screenSharingStopped'));
        }
    } else {
        return onPresentationSharingStopped(dispatch);
    }

    return false;
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 6: HANDLER FUNCTIONS — Layout Selection & Toolbar
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Handles user-selected layout change from the SelectLabel component.
 * Dispatches the layout change and persists to localStorage.
 * Validates tile view against participant count (requires >= 2 participants).
 *
 * Used by: SelectLabel.js (_onHandleViewStripState)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} layout - The selected layout (one of LAYOUTS.*).
 * @param {number} participantCount - Current number of participants.
 * @returns {boolean} Whether the layout was accepted (false if tile view was rejected).
 */
export function handleSelectLayout(dispatch: Function, layout: string, participantCount?: number): boolean {
    const visibleCount = typeof participantCount === 'number'
        ? participantCount
        : getVisibleParticipantCount(getAppState());

    trackUserInteraction();
    dispatch(filmStripViewStateHandler(layout));
    jitsiLocalStorage.setItem('filmStripView', layout);

    if (layout === LAYOUTS.TILE_VIEW && visibleCount < 2) {
        // Tile view requires >= 2 participants
        dispatch(filmStripViewStateHandler(LAYOUTS.STAGE_VIEW));

        return false;
    }

    return true;
}

/**
 * Handles the keyboard shortcut for toggling stage view.
 *
 * Used by: Toolbox.js (_onShortcutToggleStageView)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {void}
 */
export function handleToggleStageView(dispatch: Function) {
    trackUserInteraction();
    dispatch(filmStripViewStateHandler(LAYOUTS.STAGE_VIEW));
}

/**
 * Handles responsive layout adjustment in Toolbox when chat/participants pane
 * is open on narrow screens.
 *
 * Used by: Toolbox.js (_renderToolboxContent)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} filmStripView - Current filmstrip view.
 * @param {boolean} isChatOrPaneOpen - Whether chat or participants pane is open.
 * @returns {void}
 */
export function handleToolboxResponsiveFilmstrip(dispatch: Function, filmStripView: string, isChatOrPaneOpen: boolean) {
    if (getWindowWidth() < 945 && isChatOrPaneOpen && filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW) {
        dispatch(filmStripViewStateHandler(LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW));
    }
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 7: HANDLER FUNCTIONS — Feature-specific
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Handles filmstrip change when whiteboard or etherpad opens via command listener.
 * Computes the appropriate view from allowed layouts (exits tile only if non-tile allowed).
 *
 * Used by: toolbox/middleware.js (WHITEBOARD_COMMAND, ETHERPAD_COMMIT)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {void}
 */
export function handleWhiteboardOrEtherpadOpen(dispatch: Function) {
    const state = getAppState();
    const filmstripview = getSharingTargetFilmstripView(state, false);

    dispatch(filmStripViewStateHandler(filmstripview));
}

/**
 * Handles filmstrip change when a shared video starts playing.
 * Switches to the target sharing layout (Vertical -> Horizontal -> Stage -> Tile) unless PiP is active.
 *
 * Used by: shared-video/middleware.web.ts (handleSharingVideoStatus)
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} getState - Redux getState function.
 * @returns {void}
 */
export function handleSharedVideoStart(dispatch: Function, getState: Function) {
    const state = getState();

    onPresentationSharingStarted(state);
    const isPiPActive = getPictureInPictureModeConfig(state);

    if (!isPiPActive) {
        const targetView = getSharingTargetFilmstripView(state, false);

        dispatch(filmStripViewStateHandler(targetView, 'handleSharedVideoStart'));
    }
}

/**
 * Handles filmstrip change when a YouTube shared video player is ready
 * (legacy SharedVideo.js).
 * Exits tile view if allowed and non-tile layout is permitted.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {void}
 */
export function handleSharedVideoPlayerReady(dispatch: Function) {
    const state = getAppState();
    const filmstripview = getSharingTargetFilmstripView(state, false);

    dispatch(filmStripViewStateHandler(filmstripview));
}

